import React, { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { settingsService } from '../services/settingsService'
import { AuthContext } from './AuthContext'

// Theme context interface
interface ThemeContextType {
  theme: 'light' | 'dark' | 'system'
  colorMode: 'light' | 'dark'
  setTheme: (theme: 'light' | 'dark' | 'system') => Promise<void>
  toggleColorMode: () => void
}

// Create theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Use useContext directly to avoid throwing if AuthProvider is not available
  // This allows ThemeProvider to work even if auth context is not ready
  const authContext = useContext(AuthContext)
  const currentUser = authContext?.currentUser ?? null
  // Initialize theme state from localStorage immediately (synchronous)
  const getInitialTheme = (): 'light' | 'dark' | 'system' => {
    if (typeof window === 'undefined') return 'light'
    const stored = localStorage.getItem('theme-preference')
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored as 'light' | 'dark' | 'system'
    }
    return 'light'
  }

  const getInitialColorMode = (): 'light' | 'dark' => {
    const initialTheme = getInitialTheme()
    if (initialTheme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return initialTheme
  }

  const [theme, setThemeState] = useState<'light' | 'dark' | 'system'>(getInitialTheme)
  const [colorMode, setColorModeState] = useState<'light' | 'dark'>(getInitialColorMode)
  const [isInitialized, setIsInitialized] = useState(false)

  // Set initial theme immediately on mount (before async operations)
  useEffect(() => {
    const initialTheme = getInitialTheme()
    if (initialTheme === 'system') {
      const systemTheme = getInitialColorMode()
      document.documentElement.setAttribute('data-theme', systemTheme)
    } else {
      document.documentElement.setAttribute('data-theme', initialTheme)
    }
  }, [])

  // Get system theme preference
  const getSystemTheme = useCallback((): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }, [])

  // Apply theme to document
  const applyTheme = useCallback((themeValue: 'light' | 'dark' | 'system') => {
    console.log('[ThemeContext] applyTheme called:', { themeValue })
    let effectiveColorMode: 'light' | 'dark'
    
    if (themeValue === 'system') {
      effectiveColorMode = getSystemTheme()
      console.log('[ThemeContext] System theme detected:', effectiveColorMode)
    } else {
      effectiveColorMode = themeValue
    }

    console.log('[ThemeContext] Effective color mode:', effectiveColorMode)
    console.log('[ThemeContext] Current document attributes before change:', {
      'data-theme': document.documentElement.getAttribute('data-theme'),
      'data-color-mode': document.documentElement.getAttribute('data-color-mode'),
      'data-chakra-color-mode': document.documentElement.getAttribute('data-chakra-color-mode'),
      classes: document.documentElement.className,
      colorScheme: document.documentElement.style.colorScheme
    })

    // Set data-theme attribute for CSS and Chakra UI v3 semantic tokens
    // Chakra UI v3's semantic tokens with _dark variants respond to [data-theme="dark"]
    document.documentElement.setAttribute('data-theme', effectiveColorMode)
    console.log('[ThemeContext] Set data-theme:', effectiveColorMode)
    
    // Chakra UI v3 might check for data-color-mode attribute
    document.documentElement.setAttribute('data-color-mode', effectiveColorMode)
    console.log('[ThemeContext] Set data-color-mode:', effectiveColorMode)
    
    // Also set class for additional CSS targeting if needed
    document.documentElement.classList.remove('chakra-ui-light', 'chakra-ui-dark')
    document.documentElement.classList.add(`chakra-ui-${effectiveColorMode}`)
    console.log('[ThemeContext] Updated classes:', document.documentElement.className)
    
    // Update local color mode state - this will trigger a re-render
    setColorModeState(effectiveColorMode)
    console.log('[ThemeContext] Updated colorMode state:', effectiveColorMode)
    
    // Force a re-render by triggering a style recalculation
    // This ensures CSS custom properties are recalculated
    document.documentElement.style.setProperty('color-scheme', effectiveColorMode)
    console.log('[ThemeContext] Set color-scheme CSS property:', effectiveColorMode)
    
    // Dispatch a custom event to notify components of theme change
    // This can be used by components that need to react to theme changes
    window.dispatchEvent(new CustomEvent('theme-change', { detail: { theme: effectiveColorMode } }))
    console.log('[ThemeContext] Dispatched theme-change event')
    
    // Force Chakra to recalculate by toggling a data attribute
    // This might trigger Chakra's internal color mode system
    document.documentElement.setAttribute('data-chakra-color-mode', effectiveColorMode)
    console.log('[ThemeContext] Set data-chakra-color-mode:', effectiveColorMode)
    
    // Trigger a reflow to ensure Chakra's semantic tokens update
    // This forces the browser to recalculate styles
    requestAnimationFrame(() => {
      void document.documentElement.offsetHeight
      console.log('[ThemeContext] Reflow triggered, final attributes:', {
        'data-theme': document.documentElement.getAttribute('data-theme'),
        'data-color-mode': document.documentElement.getAttribute('data-color-mode'),
        'data-chakra-color-mode': document.documentElement.getAttribute('data-chakra-color-mode'),
        classes: document.documentElement.className,
        colorScheme: document.documentElement.style.colorScheme
      })
    })
  }, [getSystemTheme])

  // Initialize theme from user settings (async, only if authenticated and different from localStorage)
  useEffect(() => {
    const initializeTheme = async () => {
      // Skip if already initialized or no user
      if (isInitialized || !currentUser) {
        if (!currentUser) {
          setIsInitialized(true)
        }
        return
      }

      try {
        // Try to load from user settings
        console.log('[ThemeContext] Loading theme from backend...')
        const userSettings = await settingsService.getUserSettings()
        console.log('[ThemeContext] Backend userSettings:', userSettings)
        console.log('[ThemeContext] Backend theme:', userSettings?.appearance?.theme)
        
        const currentLocalTheme = localStorage.getItem('theme-preference') || 'light'
        console.log('[ThemeContext] Current localStorage theme:', currentLocalTheme)
        
        if (userSettings?.appearance?.theme) {
          const backendTheme = userSettings.appearance.theme
          // Only update if different from localStorage
          if (backendTheme !== currentLocalTheme) {
            console.log('[ThemeContext] Backend theme differs from localStorage, applying backend theme:', backendTheme)
            setThemeState(backendTheme)
            applyTheme(backendTheme)
            // Update localStorage to match backend
            localStorage.setItem('theme-preference', backendTheme)
          } else {
            console.log('[ThemeContext] Backend theme matches localStorage, no change needed')
          }
        } else {
          console.log('[ThemeContext] No theme in backend settings, keeping localStorage theme:', currentLocalTheme)
        }
        setIsInitialized(true)
      } catch (error) {
        console.debug('ThemeContext: Could not load theme from user settings:', error)
        // Keep using localStorage theme, just mark as initialized
        setIsInitialized(true)
      }
    }

    initializeTheme()
  }, [currentUser, applyTheme, isInitialized])

  // Listen for system theme changes when theme is set to "system"
  useEffect(() => {
    if (theme !== 'system' || !isInitialized) return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      const newColorMode = e.matches ? 'dark' : 'light'
      document.documentElement.setAttribute('data-theme', newColorMode)
      document.documentElement.classList.remove('chakra-ui-light', 'chakra-ui-dark')
      document.documentElement.classList.add(`chakra-ui-${newColorMode}`)
      document.documentElement.style.setProperty('color-scheme', newColorMode)
      setColorModeState(newColorMode)
      // Trigger reflow to ensure styles update
      void document.documentElement.offsetHeight
    }

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange)
      return () => mediaQuery.removeEventListener('change', handleSystemThemeChange)
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange)
      return () => mediaQuery.removeListener(handleSystemThemeChange)
    }
  }, [theme, isInitialized])

  // Set theme and persist to backend/localStorage
  const setTheme = useCallback(async (newTheme: 'light' | 'dark' | 'system') => {
    console.log('[ThemeContext] setTheme called:', { newTheme, currentTheme: theme, currentColorMode: colorMode })
    setThemeState(newTheme)
    console.log('[ThemeContext] Theme state updated, calling applyTheme...')
    applyTheme(newTheme)
    console.log('[ThemeContext] applyTheme completed')
    
    // Persist to localStorage
    localStorage.setItem('theme-preference', newTheme)
    console.log('[ThemeContext] Saved to localStorage:', newTheme)
    
    // Persist to backend if authenticated
    if (currentUser) {
      try {
        console.log('[ThemeContext] Saving to backend...')
        await settingsService.updateAppearance({ theme: newTheme })
        console.log('[ThemeContext] Backend save successful')
      } catch (error) {
        console.error('ThemeContext: Failed to save theme to backend:', error)
        // Continue anyway - theme is already applied
      }
    } else {
      console.log('[ThemeContext] No currentUser, skipping backend save')
    }
  }, [currentUser, applyTheme, theme, colorMode])

  // Toggle between light and dark (not system)
  const toggleColorMode = useCallback(() => {
    const newTheme = colorMode === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }, [colorMode, setTheme])

  // Get effective color mode (resolves system to actual light/dark)
  const effectiveColorMode = colorMode

  const value: ThemeContextType = {
    theme,
    colorMode: effectiveColorMode,
    setTheme,
    toggleColorMode,
  }

  // Render children immediately - theme is set synchronously on mount
  // The async initialization will update the theme if needed from backend
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

